import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { deepEqual, deepMerge, useIsomorphicLayoutEffect } from '@wp-g2/utils';
import { isNil } from 'lodash';
import React, { createContext, useContext, useRef, useState } from 'react';
export var ComponentsContext = /*#__PURE__*/createContext({});
export var useComponentsContext = function () {
  return useContext(ComponentsContext);
};
/**
 * Consolidates incoming ContextSystem values with a (potential) parent ContextSystem value.
 *
 * @param {object} props
 * @param {object} props.value
 * @return {object}
 */

function useContextSystemBridge(_ref) {
  var value = _ref.value;

  if (isNil(value)) {
    // @ts-ignore
    value = {};
  }

  var parentContext = useComponentsContext();
  var parentContextRef = useRef(parentContext);
  var valueRef = useRef(deepMerge(parentContext, value));

  var _useState = useState(valueRef.current),
      _useState2 = _slicedToArray(_useState, 2),
      config = _useState2[0],
      setConfig = _useState2[1];

  useIsomorphicLayoutEffect(function () {
    var hasChange = false;

    if (!deepEqual(value, valueRef.current)) {
      valueRef.current = value;
      hasChange = true;
    }

    if (!deepEqual(parentContext, parentContextRef.current)) {
      valueRef.current = deepMerge(parentContext, valueRef.current);
      parentContextRef.current = parentContext;
      hasChange = true;
    }

    if (hasChange) {
      setConfig(function (prev) {
        return _objectSpread(_objectSpread({}, prev), valueRef.current);
      });
    }
  }, [value, parentContext]);
  return config;
}
/**
 * A Provider component that can modify props for connected components within
 * the Context system.
 *
 * @example
 * ```jsx
 * <ContextSystemProvider value={{ Button: { size: 'small' }}}>
 *   <Button>...</Button>
 * </ContextSystemProvider>
 * ```
 *
 * @template {Record<string, any>} T
 * @param {object} options
 * @param {import('react').ReactNode} options.children Children to render.
 * @param {T} options.value Props to render into connected components.
 * @returns {JSX.Element} A Provider wrapped component.
 */


var BaseContextSystemProvider = function (_ref2) {
  var children = _ref2.children,
      value = _ref2.value;
  var contextValue = useContextSystemBridge({
    value: value
  });
  return /*#__PURE__*/React.createElement(ComponentsContext.Provider, {
    value: contextValue
  }, children);
};

export var ContextSystemProvider = /*#__PURE__*/React.memo(BaseContextSystemProvider);